import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, Globe, Mail, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintWholesaleInvoice() {
  const { id, businessSlug } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const API = `${BASE_API}/b/${businessSlug}`;

  // Allow pinch-zoom on this page
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport?.getAttribute('content');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=0.5, maximum-scale=3, user-scalable=yes');
    }
    return () => {
      if (viewport && originalContent) {
        viewport.setAttribute('content', originalContent);
      }
    };
  }, []);

  useEffect(() => {
    toast.dismiss();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [invoiceRes, businessRes, settingsRes] = await Promise.all([
        axios.get(`${API}/wholesale/invoices/${id}`),
        axios.get(`${API}/info`),
        axios.get(`${API}/settings`).catch(() => ({ data: null }))
      ]);
      setInvoice(invoiceRes.data);
      setBusinessInfo(businessRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [id, API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('printable-invoice');
    const invoiceNum = invoice?.invoice_number || 'invoice';
    
    const opt = {
      margin: 0.25,
      filename: `${invoiceNum}-${business.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'ILKSOY'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    toast.loading('Generating PDF...');
    html2pdf().set(opt).from(element).save().then(() => {
      toast.dismiss();
      toast.success('Invoice downloaded!');
    }).catch((err) => {
      toast.dismiss();
      toast.error('Failed to generate PDF');
      console.error(err);
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading invoice...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Invoice not found
      </div>
    );
  }

  const business = businessInfo || {
    name: businessSlug?.toUpperCase() || "Business",
    address: "",
    phone: ""
  };

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            img { max-width: 100% !important; }
            #printable-invoice { margin: 0 !important; padding: 0.25in !important; min-width: unset !important; }
            #invoice-container { overflow: visible !important; }
          }
          /* Mobile: Allow zoom out with pinch, show full invoice */
          @media screen and (max-width: 900px) {
            html {
              touch-action: manipulation;
            }
            #invoice-container {
              overflow-x: auto;
              overflow-y: visible;
              -webkit-overflow-scrolling: touch;
            }
            #printable-invoice {
              min-width: 750px !important;
              transform-origin: top left;
            }
          }
        `}
      </style>
      
      {/* Mobile zoom hint */}
      <div className="no-print lg:hidden" style={{ 
        padding: '8px 16px', 
        background: '#1e293b', 
        color: '#94a3b8', 
        fontSize: '12px',
        textAlign: 'center',
        borderBottom: '1px solid #334155'
      }}>
        📱 Pinch to zoom • Scroll to see full invoice
      </div>
      
      {/* Back button - hidden when printing */}
      <div className="no-print" style={{ padding: '16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Link to={`/${businessSlug}/wholesale`}>
          <Button variant="outline" size="sm" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#22d3ee', borderColor: '#0891b2', background: 'transparent' }}>
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back
          </Button>
        </Link>
        <Button 
          onClick={handlePrint} 
          size="sm"
          style={{ background: 'linear-gradient(to right, #06b6d4, #3b82f6)', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Printer style={{ width: '16px', height: '16px' }} />
          Print
        </Button>
        <Button 
          onClick={handleDownloadPDF}
          size="sm"
          style={{ background: 'linear-gradient(to right, #10b981, #059669)', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Download style={{ width: '16px', height: '16px' }} />
          Download PDF
        </Button>
        <Button 
          onClick={async () => {
            // Always ask for email - pre-fill with consignee email but allow user to change
            const defaultEmail = invoice?.consignee_email || '';
            const email = prompt('Send invoice to email address:', defaultEmail);
            if (!email || !email.trim()) {
              toast.error('Please enter an email address');
              return;
            }
            try {
              toast.loading('Sending invoice...');
              await axios.post(`${API}/wholesale/invoices/${id}/email?recipient_email=${encodeURIComponent(email.trim())}`);
              toast.dismiss();
              toast.success(`Invoice sent to ${email.trim()}`);
            } catch (err) {
              toast.dismiss();
              toast.error(err.response?.data?.detail || 'Failed to send email');
            }
          }}
          size="sm"
          variant="outline"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#22d3ee', borderColor: '#0891b2', background: 'transparent' }}
        >
          <Mail style={{ width: '16px', height: '16px' }} />
          Email
        </Button>
      </div>

      {/* Scrollable Container for Mobile */}
      <div id="invoice-container" style={{ background: '#f1f5f9', padding: '20px', minHeight: 'calc(100vh - 60px)' }}>
        {/* Printable Invoice */}
        <div id="printable-invoice" style={{
        width: '100%',
        maxWidth: '8.5in',
        minHeight: '10in',
        margin: '0 auto',
        padding: '0.5in',
        background: 'white',
        color: 'black',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        lineHeight: '1.4',
        display: 'flex',
        flexDirection: 'column'
      }}>
        
        {/* Header with Logo and Business Name */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          borderBottom: '3px solid #0891b2',
          paddingBottom: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Logo */}
            <div style={{
              width: '90px',
              height: '90px',
              background: businessInfo?.logo_base64 || businessInfo?.logo_url ? '#fff' : 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {(businessInfo?.logo_base64 || businessInfo?.logo_url) ? (
                <img 
                  src={businessInfo?.logo_base64 || businessInfo?.logo_url} 
                  alt="Business Logo"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}
                  onError={(e) => { 
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="color: white; font-size: 24px;">🌐</div>';
                  }}
                />
              ) : (
                <div style={{ color: 'white', fontSize: '36px' }}>🌐</div>
              )}
            </div>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#0f172a' }}>
                {business.name?.toUpperCase()}
              </h1>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#0891b2', fontWeight: '600', letterSpacing: '1px' }}>
                IMPORT & DISTRIBUTION
              </p>
              {business.address && (
                <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                  {business.address}
                </p>
              )}
              {business.phone && (
                <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                  Phone: {business.phone}
                </p>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, color: '#0f172a' }}>INVOICE</h2>
            <p style={{ margin: '4px 0', fontSize: '16px', fontWeight: 'bold', color: '#0891b2' }}>{invoice.invoice_number}</p>
            <p style={{ margin: '2px 0', fontSize: '11px', color: '#64748b' }}>
              Date: {new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString()}
            </p>
            {invoice.due_date && (
              <p style={{ margin: '2px 0', fontSize: '11px', color: '#64748b' }}>
                Due: {new Date(invoice.due_date).toLocaleDateString()}
              </p>
            )}
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '12px', 
              fontWeight: 'bold',
              padding: '4px 12px',
              background: invoice.payment_status === 'paid' ? '#dcfce7' : invoice.payment_status === 'partial' ? '#dbeafe' : '#fef3c7',
              color: invoice.payment_status === 'paid' ? '#166534' : invoice.payment_status === 'partial' ? '#1e40af' : '#92400e',
              display: 'inline-block',
              borderRadius: '4px'
            }}>
              {invoice.payment_status.toUpperCase()}
            </p>
          </div>
        </div>

        {/* Bill To & Ship Info */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '9px', fontWeight: 'bold', margin: '0 0 6px 0', color: '#0891b2', textTransform: 'uppercase', letterSpacing: '1px' }}>Consignee (Bill To):</p>
            <p style={{ margin: '0', fontWeight: 'bold', fontSize: '14px' }}>{invoice.consignee_company || invoice.consignee_name}</p>
            {invoice.consignee_company && invoice.consignee_name && (
              <p style={{ margin: '2px 0', fontSize: '11px', color: '#475569' }}>Attn: {invoice.consignee_name}</p>
            )}
            {invoice.consignee_address && <p style={{ margin: '2px 0', fontSize: '11px', color: '#64748b' }}>{invoice.consignee_address}</p>}
            {invoice.consignee_phone && <p style={{ margin: '2px 0', fontSize: '11px', color: '#64748b' }}>Phone: {invoice.consignee_phone}</p>}
            {invoice.consignee_email && <p style={{ margin: '2px 0', fontSize: '11px', color: '#64748b' }}>{invoice.consignee_email}</p>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '9px', fontWeight: 'bold', margin: '0 0 6px 0', color: '#0891b2', textTransform: 'uppercase', letterSpacing: '1px' }}>Shipping Info:</p>
            {invoice.ship_from_warehouse && (
              <p style={{ margin: '0', fontSize: '11px' }}>Ship From: <strong>{invoice.ship_from_warehouse}</strong></p>
            )}
            {invoice.final_destination && (
              <p style={{ margin: '2px 0', fontSize: '11px' }}>Destination: {invoice.final_destination}</p>
            )}
            {invoice.bill_of_lading_ref && (
              <p style={{ margin: '2px 0', fontSize: '11px' }}>BOL: {invoice.bill_of_lading_ref}</p>
            )}
            <p style={{ margin: '6px 0 0 0', fontSize: '11px', fontWeight: 'bold' }}>
              Terms: {invoice.payment_terms?.replace('_', ' ').toUpperCase()}
            </p>
          </div>
        </div>

        {/* Line Items Table */}
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          marginBottom: '16px'
        }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '35px', textTransform: 'uppercase', color: '#0f172a' }}>#</th>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '50px', textTransform: 'uppercase', color: '#0f172a' }}>Image</th>
              <th style={{ padding: '10px 6px', textAlign: 'left', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', textTransform: 'uppercase', color: '#0f172a' }}>Description</th>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '65px', textTransform: 'uppercase', color: '#0f172a' }}>SKU</th>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '50px', textTransform: 'uppercase', color: '#0f172a' }}>Cases</th>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '55px', textTransform: 'uppercase', color: '#0f172a' }}>Qty/Case</th>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '60px', textTransform: 'uppercase', color: '#0f172a' }}>Total Units</th>
              <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '75px', textTransform: 'uppercase', color: '#0f172a' }}>Unit Price</th>
              <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #0891b2', width: '85px', textTransform: 'uppercase', color: '#0f172a' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items?.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#0891b2' }}>{index + 1}</td>
                <td style={{ padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.name || 'Product'} 
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        objectFit: 'cover', 
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }} 
                    />
                  ) : (
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      background: '#f1f5f9', 
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      border: '1px solid #e2e8f0'
                    }}>
                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>-</span>
                    </div>
                  )}
                </td>
                <td style={{ padding: '10px 6px', fontSize: '11px' }}>{item.name || item.description}</td>
                <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '9px', fontFamily: 'monospace', color: '#0891b2' }}>{item.sku || '-'}</td>
                <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '500' }}>{item.cases || '-'}</td>
                <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '11px', color: '#475569' }}>{item.qty_per_case || '-'}</td>
                <td style={{ padding: '10px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600' }}>{item.quantity_units || item.quantity || 0}</td>
                <td style={{ padding: '10px 6px', textAlign: 'right', fontSize: '11px' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ padding: '10px 6px', textAlign: 'right', fontSize: '11px', fontWeight: '600' }}>{formatCurrency(item.total || ((item.quantity_units || item.quantity || 0) * item.unit_price))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto' }}>
          {/* Order Summary */}
          <div style={{ flex: 1 }}>
            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <p style={{ fontSize: '9px', fontWeight: 'bold', margin: '0 0 8px 0', color: '#0891b2', textTransform: 'uppercase' }}>Order Summary</p>
              <div style={{ display: 'flex', gap: '24px' }}>
                <div>
                  <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>Total Units</p>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '2px 0 0 0' }}>{invoice.items?.reduce((sum, item) => sum + (item.quantity_units || item.quantity || 0), 0)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>Total Cases</p>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '2px 0 0 0' }}>{invoice.items?.reduce((sum, item) => sum + (item.cases || 0), 0) || '-'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>Line Items</p>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '2px 0 0 0' }}>{invoice.items?.length || 0}</p>
                </div>
              </div>
            </div>
            
            {invoice.notes && (
              <div style={{ marginTop: '12px' }}>
                <p style={{ fontSize: '9px', fontWeight: 'bold', margin: '0 0 4px 0', color: '#64748b', textTransform: 'uppercase' }}>Notes:</p>
                <p style={{ fontSize: '10px', color: '#475569', margin: 0 }}>{invoice.notes}</p>
              </div>
            )}
          </div>

          {/* Totals */}
          <div style={{ width: '220px', marginLeft: '24px' }}>
            <div style={{ borderBottom: '1px solid #e2e8f0', padding: '6px 0', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '11px', color: '#64748b' }}>Subtotal:</span>
              <span style={{ fontSize: '11px', fontWeight: '500' }}>{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.tax_amount > 0 && (
              <div style={{ borderBottom: '1px solid #e2e8f0', padding: '6px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '11px', color: '#64748b' }}>Tax ({(invoice.tax_rate * 100).toFixed(1)}%):</span>
                <span style={{ fontSize: '11px' }}>{formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'space-between', background: '#0f172a', margin: '8px -8px', paddingLeft: '8px', paddingRight: '8px', borderRadius: '4px' }}>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>TOTAL:</span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#22d3ee' }}>{formatCurrency(invoice.total)}</span>
            </div>
            {invoice.amount_paid > 0 && invoice.payment_status !== 'paid' && (
              <>
                <div style={{ padding: '6px 0', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '11px', color: '#16a34a' }}>Amount Paid:</span>
                  <span style={{ fontSize: '11px', color: '#16a34a' }}>-{formatCurrency(invoice.amount_paid)}</span>
                </div>
                <div style={{ padding: '6px 0', display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #fbbf24' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#d97706' }}>Balance Due:</span>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#d97706' }}>{formatCurrency(invoice.total - invoice.amount_paid)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          {/* 50/50 Payment note - if enabled */}
          {invoice.show_50_percent_terms && (
            <p style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#475569', 
              margin: '0 0 12px 0'
            }}>
              Payment: 50% now, 50% on delivery
            </p>
          )}
          <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 4px 0' }}>
            Thank you for your business!
          </p>
          <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>
            {business.name} • {business.address || 'Import & Distribution'} • {business.phone || ''}
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
